import type { PrecioTipo } from '../shared/types/autorizacion';
import type { GrainGroup } from '../shared/types/grain';
import type { FormInputValues } from '../shared/types/venta-form';
import { $ } from '../shared/utils/dom';
import { fmtKg } from '../shared/utils/format';
import { readLocaleNumberField } from '../shared/utils/locale-number-input';
import { toast } from '../shared/utils/toast';
import type { EmissionUiLike } from './emission-ui';
import { isValidVencimientoDias } from './emission-ui';

export function setVencimientoDias(
  dias: number,
  emission: EmissionUiLike,
  precioTipo: PrecioTipo
): number {
  const normalized = isValidVencimientoDias(dias) ? dias : 7;
  document.querySelectorAll<HTMLButtonElement>('#vencimiento-segment .segment-btn--venc').forEach(
    (btn) => {
      btn.classList.toggle(
        'active',
        Number(btn.getAttribute('data-vencimiento')) === normalized
      );
    }
  );
  emission.renderVencimientoInfo(precioTipo, normalized);
  return normalized;
}

export function syncPrecioTipoUI(
  precioTipo: PrecioTipo,
  vencimientoDias: number | null,
  emission: EmissionUiLike
): number | null {
  const isBase = precioTipo === 'base';
  $('precio-field')?.classList.toggle('hidden', !isBase);
  $('vencimiento-field')?.classList.toggle('hidden', !isBase);
  const precioInput = $('precio') as HTMLInputElement | null;
  if (!isBase) {
    if (precioInput) precioInput.value = '';
    emission.renderVencimientoInfo(precioTipo, null);
    return null;
  }
  const dias = setVencimientoDias(vencimientoDias || 7, emission, precioTipo);
  return dias;
}

export interface UpdateGrainUiOptions {
  /** Panel admin: puede autorizar por encima de la disponibilidad. */
  adminAllowZeroDisponible?: boolean;
}

export function updateGrainUI(
  selectedGrain: GrainGroup | null,
  options?: UpdateGrainUiOptions
): void {
  const hint = $('disponibilidad-hint');
  const badge = $('saldo-negativo-badge');
  if (!selectedGrain) {
    if (hint) hint.textContent = '';
    badge?.classList.add('hidden');
    return;
  }
  if (hint) {
    const reserved = selectedGrain.pendingReservedKg ?? 0;
    if (selectedGrain.availableKg <= 0 && options?.adminAllowZeroDisponible) {
      hint.textContent =
        reserved > 0
          ? 'Sin kilos netos (descontadas ' +
            fmtKg(reserved) +
            ' kg en pendientes/en proceso). Podés autorizar igual como operador del panel.'
          : 'Sin kilos netos en la cuenta. Podés autorizar igual si hay cierre mercado o lo justifica la operación.';
    } else {
      hint.textContent =
        reserved > 0
          ? 'Disponible: ' +
            fmtKg(selectedGrain.availableKg) +
            ' kg (descontadas ' +
            fmtKg(reserved) +
            ' kg en pendientes/en proceso vigentes)'
          : 'Disponible: ' + fmtKg(selectedGrain.availableKg) + ' kg';
    }
  }
  const saldo = selectedGrain.balanceTotal - selectedGrain.availableKg;
  if (badge) {
    if (saldo < 0) {
      badge.textContent = 'Saldo negativo: ' + fmtKg(saldo) + ' kg';
      badge.classList.remove('hidden');
    } else {
      badge.classList.add('hidden');
    }
  }
}

export interface ValidateFormInputsOptions {
  /** Panel admin: operadores pueden autorizar por encima de la disponibilidad. */
  allowExceedAvailability?: boolean;
}

export function validateFormInputs(
  selectedGrain: GrainGroup | null,
  precioTipo: PrecioTipo,
  vencimientoDias: number | null,
  emission: EmissionUiLike,
  options?: ValidateFormInputsOptions
): FormInputValues | null {
  if (!selectedGrain) {
    toast('Seleccioná un grano.');
    return null;
  }

  const kilos = readLocaleNumberField('kilos');
  if (kilos == null || kilos <= 0) {
    toast('Ingresá los kilos.');
    return null;
  }
  if (!options?.allowExceedAvailability && kilos > selectedGrain.availableKg) {
    toast('Los kilos no pueden superar la disponibilidad.');
    return null;
  }

  const precioRaw = readLocaleNumberField('precio');
  const precio = precioTipo === 'base' ? precioRaw : null;
  if (precioTipo === 'base' && (!precio || precio <= 0)) {
    toast('Ingresá el precio base.');
    return null;
  }

  if (precioTipo === 'base' && !isValidVencimientoDias(vencimientoDias)) {
    toast('Seleccioná el vencimiento de la orden.');
    return null;
  }
  if (!emission.schedule?.fechaEmision) {
    toast('No se pudo determinar la fecha de emisión. Recargá la página.');
    return null;
  }

  return { kilos, precio, vencimientoDias };
}

/** Valor del &lt;select id="grain"&gt; para abrir campaña manual (solo admin). */
export const GRAIN_SELECT_VALUE_MANUAL = '__manual_campaign__';

export interface RenderGrainSelectOptions {
  /** Muestra cereales agregados solo por cierre mercado (0 kg netos). */
  showCierreMercadoHint?: boolean;
  /** Última opción: elegir campaña del productor a mano. */
  manualCampaignOption?: boolean;
}

let grainPickerSelectedIndex = 0;
let grainPickerBound = false;
let grainPickerChangeHandler: (() => void) | null = null;

function grainOptionLabel(g: GrainGroup, options?: RenderGrainSelectOptions): string {
  const cierreOnly =
    options?.showCierreMercadoHint &&
    g.availableKg <= 0 &&
    (!g.cosechas || g.cosechas.length === 0);
  const suffix = cierreOnly ? ' · cierre mercado' : '';
  return g.grain + ' — ' + fmtKg(g.availableKg) + ' kg' + suffix;
}

export function getSelectedGrainIndex(): number {
  return grainPickerSelectedIndex;
}

export function setSelectedGrainIndex(index: number): void {
  grainPickerSelectedIndex = Math.max(0, index);
  syncGrainPickerUi();
  grainPickerChangeHandler?.();
}

function syncGrainPickerUi(): void {
  const label = $('grain-trigger-label');
  const list = $('grain-list');
  const selectedBtn = list?.querySelector<HTMLElement>(
    `.grain-picker__option[data-grain-index="${grainPickerSelectedIndex}"] .grain-picker__option-text`
  );
  if (label) {
    label.textContent = selectedBtn?.textContent?.trim() || 'Seleccionar grano';
  }
  if (!list) return;
  list.querySelectorAll<HTMLButtonElement>('.grain-picker__option').forEach((btn) => {
    const idx = Number(btn.getAttribute('data-grain-index'));
    const selected = idx === grainPickerSelectedIndex;
    btn.setAttribute('aria-selected', selected ? 'true' : 'false');
  });
}

function closeGrainPickerSheet(): void {
  $('grain-sheet')?.classList.add('hidden');
  const trigger = $('grain-trigger') as HTMLButtonElement | null;
  trigger?.setAttribute('aria-expanded', 'false');
}

function openGrainPickerSheet(): void {
  $('grain-sheet')?.classList.remove('hidden');
  const trigger = $('grain-trigger') as HTMLButtonElement | null;
  trigger?.setAttribute('aria-expanded', 'true');
}

function bindGrainPicker(grains: GrainGroup[], options?: RenderGrainSelectOptions): void {
  if (grainPickerBound) return;
  grainPickerBound = true;

  $('grain-trigger')?.addEventListener('click', () => openGrainPickerSheet());
  $('grain-sheet-backdrop')?.addEventListener('click', () => closeGrainPickerSheet());
  $('grain-sheet-cancel')?.addEventListener('click', () => closeGrainPickerSheet());

  $('grain-list')?.addEventListener('click', (ev) => {
    const btn = (ev.target as HTMLElement).closest<HTMLButtonElement>('.grain-picker__option');
    if (!btn) return;
    const idx = Number(btn.getAttribute('data-grain-index'));
    if (Number.isNaN(idx)) return;
    setSelectedGrainIndex(idx);
    closeGrainPickerSheet();
  });
}

export function onGrainPickerChange(handler: () => void): void {
  grainPickerChangeHandler = handler;
}

export function renderGrainSelect(
  grains: GrainGroup[],
  options?: RenderGrainSelectOptions
): void {
  const list = $('grain-list');
  if (list) {
    bindGrainPicker(grains, options);
    list.innerHTML = '';
    grains.forEach((g, i) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'grain-picker__option';
      btn.setAttribute('role', 'option');
      btn.setAttribute('data-grain-index', String(i));
      btn.innerHTML =
        '<span class="grain-picker__option-text"></span><span class="grain-picker__option-mark" aria-hidden="true"></span>';
      const text = btn.querySelector('.grain-picker__option-text');
      if (text) text.textContent = grainOptionLabel(g, options);
      list.appendChild(btn);
    });
    if (grainPickerSelectedIndex >= grains.length) {
      grainPickerSelectedIndex = 0;
    }
    if (grains.length) {
      syncGrainPickerUi();
    } else {
      const label = $('grain-trigger-label');
      if (label) label.textContent = 'Seleccionar grano';
    }
    return;
  }

  const sel = $('grain') as HTMLSelectElement | null;
  if (!sel) return;
  const prevValue = sel.value;
  sel.innerHTML = '';
  grains.forEach((g, i) => {
    const opt = document.createElement('option');
    opt.value = String(i);
    opt.textContent = grainOptionLabel(g, options);
    sel.appendChild(opt);
  });
  if (options?.manualCampaignOption) {
    const manual = document.createElement('option');
    manual.value = GRAIN_SELECT_VALUE_MANUAL;
    manual.textContent = 'Seleccionar campaña manual…';
    sel.appendChild(manual);
  }
  if (prevValue === GRAIN_SELECT_VALUE_MANUAL && options?.manualCampaignOption) {
    sel.value = GRAIN_SELECT_VALUE_MANUAL;
  } else if (prevValue && grains.some((_, i) => String(i) === prevValue)) {
    sel.value = prevValue;
  } else if (grains.length) {
    sel.selectedIndex = 0;
  } else if (options?.manualCampaignOption) {
    sel.value = GRAIN_SELECT_VALUE_MANUAL;
  }
}

export function renderAccountDisplay(accountId: string, accountLabel: string): void {
  const display = accountLabel ? '(' + accountId + ') ' + accountLabel : accountId;
  const el = $('account-display');
  if (el) el.innerHTML = '<span>Cuenta</span> ' + display;
}
